import React, { useState, useEffect } from "react";
import * as Icon from "react-feather";
import {
  Button,
  Form,
  Input,
  Switch,
  Modal,
  Tabs,
  Select,
  Row,
  Col,
  Divider,
  InputNumber,
  AutoComplete,
} from "antd";
import { useDispatch } from "react-redux";
import programService from "../../services/program-service";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import {
  VISIBILITY_DEFAULT,
  DEGREE_LEVEL_OPTIONS,
} from "../../constants/app-constants";
import VisibilityFormItem from "../../components/input/visibility-form-item";
import { FORM_LANG, formText, langOfTab } from "../../constants/form-i18n";

const { TabPane } = Tabs;
const { TextArea } = Input;

const AddEditProgramDialog = ({
  isShow,
  onCancel,
  onSaveComplete,
  editData,
  facultyOptions = [],
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // NMU-444: the active tab drives the language of the whole dialog.
  const [lang, setLang] = useState(FORM_LANG.TH);
  const t = formText(lang);

  useEffect(() => {
    if (isShow && editData) {
      form.setFieldsValue({
        titleTH: editData.title?.titleTH,
        titleEN: editData.title?.titleEN,
        descriptionTH: editData.shortDescription?.descriptionTH,
        descriptionEN: editData.shortDescription?.descriptionEN,
        degreeLevel: editData.degreeLevel ? editData.degreeLevel : undefined,
        faculty: editData.faculty,
        durationTH: editData.durationTH,
        durationEN: editData.durationEN,
        credits: editData.credits,
        applicationUrl: editData.applicationUrl,
        careersTH: editData.careersTH || [],
        careersEN: editData.careersEN || [],
        status: !!editData.status,
        visibility: editData.visibility ? editData.visibility : VISIBILITY_DEFAULT,
      });
    } else if (isShow) {
      form.setFieldsValue({
        titleTH: null,
        titleEN: null,
        descriptionTH: null,
        descriptionEN: null,
        degreeLevel: undefined,
        faculty: null,
        durationTH: null,
        durationEN: null,
        credits: null,
        applicationUrl: undefined,
        careersTH: [],
        careersEN: [],
        status: true,
        visibility: VISIBILITY_DEFAULT,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, editData]);

  const buildBean = (values) => {
    const base = editData ? JSON.parse(JSON.stringify(editData)) : {};
    return {
      ...base,
      programId: editData ? editData.programId : undefined,
      title: {
        titleTH: values.titleTH,
        titleEN: values.titleEN,
      },
      shortDescription: {
        descriptionTH: values.descriptionTH,
        descriptionEN: values.descriptionEN,
      },
      // NMU-515: `content` and `facultyName` are no longer sent. `base` spreads
      // the record being edited, so an existing programme keeps whatever it
      // already had in Mongo rather than having it blanked by a save — the
      // fields are retired from the form, not deleted from the data.
      degreeLevel: values.degreeLevel,
      faculty: values.faculty,
      durationTH: values.durationTH,
      durationEN: values.durationEN,
      credits: values.credits === undefined || values.credits === null ? null : Number(values.credits),
      applicationUrl: values.applicationUrl ? values.applicationUrl.trim() : null,
      careersTH: values.careersTH || [],
      careersEN: values.careersEN || [],
      totalView: editData && editData.totalView ? editData.totalView : 0,
      status: !!values.status,
      visibility: values.visibility,
    };
  };

  const onSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      try {
        dispatch(appLoaderAction.enable());

        const bean = buildBean(values);

        if (!editData) {
          await programService.add(bean);
        } else {
          await programService.edit(editData.programId, bean);
        }

        dispatch(appLoaderAction.disale());
        onSaveComplete();
        notifyUtitls.success(t('successTitle'), t('saveSuccess'));
      } catch (err) {
        dispatch(appLoaderAction.disale());
        notifyUtitls.error(err);
      }
    } catch (errorInfo) {
      notifyUtitls.warning(t('warningTitle'), t('incompleteForm'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container h-100">
      <div className="title-page">
        <Modal
          title={t(editData ? 'editProgram' : 'addProgram')}
          centered
          width={750}
          visible={isShow}
          onCancel={onCancel}
          destroyOnClose
          footer={[
            <Button key="submit" type="primary" onClick={onSave} loading={loading}>
              <Icon.Save className="me-2" size={17} />
              {t('save')}
            </Button>,
            <Button key="back" className="btn-gray" onClick={onCancel}>
              {t('cancel')}
            </Button>,
          ]}
        >
          <Form
            layout="vertical"
            name="add_edit_program"
            preserve={false}
            form={form}
            initialValues={{
              status: true,
              visibility: VISIBILITY_DEFAULT,
            }}
            validateMessages={{
              required: t('required'),
            }}
          >
            {/*
              forceRender: antd only mounts a tab pane the first time it is
              opened, and this Form runs with preserve={false}. Together that
              means an editor who opens an existing programme, edits something
              on the Thai tab and saves without ever clicking English would
              submit the English fields as empty and wipe them. Mounting both
              panes up front is what makes each tab safe to fill on its own.
            */}
            <Tabs defaultActiveKey="1" onChange={(key) => setLang(langOfTab(key))}>
              <TabPane tab={t('tabThai')} key="1" forceRender>
                <Form.Item
                  label={t('programTitleTH')}
                  name="titleTH"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item label={t('shortDescriptionTH')} name="descriptionTH">
                  <TextArea rows={3} />
                </Form.Item>
                {/* NMU-515: เนื้อหาหลักสูตร and ชื่อคณะ removed. The programme
                    listing on the site renders neither — it shows the title,
                    short description, duration, credits and careers — so both
                    were fields an editor filled in and never saw again. The
                    faculty *code* below stays: that one is what the listing
                    filters by. */}
                <Row gutter={20}>
                  <Col xs={24} md={12}>
                    <Form.Item label={t('durationTH')} name="durationTH">
                      <Input placeholder="เช่น 4 ปี" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label={t('careersTH')} name="careersTH">
                      <Select mode="tags" tokenSeparators={[","]} placeholder="พิมพ์แล้วกด Enter" />
                    </Form.Item>
                  </Col>
                </Row>
              </TabPane>
              <TabPane tab={t('tabEnglish')} key="2" forceRender>
                <Form.Item label="Course Title (EN)" name="titleEN" className="mb-4">
                  <Input />
                </Form.Item>
                <Form.Item label="Short Description (EN)" name="descriptionEN">
                  <TextArea rows={3} />
                </Form.Item>
                {/* NMU-515: English equivalents removed alongside the Thai ones. */}
                <Row gutter={20}>
                  <Col xs={24} md={12}>
                    <Form.Item label="Duration (EN)" name="durationEN">
                      <Input placeholder="e.g. 4 Years" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Career Paths (EN)" name="careersEN">
                      <Select mode="tags" tokenSeparators={[","]} placeholder="Type and press Enter" />
                    </Form.Item>
                  </Col>
                </Row>
              </TabPane>
            </Tabs>

            {/*
              NMU-415: each tab now holds only that language's own fields.

              Degree level, faculty code and total credits are single values
              shared by both languages, so they sit here rather than being
              repeated inside each tab — the ticket's own rule is that anything
              used by both languages belongs outside the tabs. Keeping them
              outside also means they stay visible whichever tab is open, so an
              editor working in English never has to switch back to fill them,
              and there is only ever one input bound to each value.
            */}
            <Divider className="mt-2 mb-3" orientation="left" plain>
              {t('sharedProgramInfo')}
            </Divider>

            <Row gutter={20}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('degreeLevel')}
                  name="degreeLevel"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select placeholder={t('selectDegreeLevel')}>
                    {DEGREE_LEVEL_OPTIONS.map((opt) => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('facultyCode')}
                  name="faculty"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  {/* No faculty master-data endpoint is exposed to the admin UI yet,
                      so this is a free-text key with suggestions from existing records. */}
                  <AutoComplete
                    options={facultyOptions.map((item) => ({ value: item.value }))}
                    filterOption={(input, option) =>
                      option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={t('totalCredits')} name="credits">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            {/*
              NMU-471: the admissions link for this programme, behind the
              "สมัครเข้าเรียนปีการศึกษานี้" button on the website. Optional —
              not every programme is open for application, and the website hides
              the button rather than sending people somewhere generic.
            */}
            <Form.Item
              label={t('applicationUrl')}
              name="applicationUrl"
              extra={t('applicationUrlHelp')}
              rules={[{ type: 'url', message: t('applicationUrlInvalid') }]}
            >
              <Input type="url" placeholder="https://admission.nmu.ac.th/..." />
            </Form.Item>

            {/* NMU-415: Majors, image, keywords, URL/URL Name and the open-in-
                new-tab toggle are removed — the website program page doesn't use
                them; the form now matches the confirmed field mapping. */}

            <Divider className="mt-2" />

            <Form.Item label={t('status')} name="status" valuePropName="checked">
              <Switch />
            </Form.Item>

            <VisibilityFormItem lang={lang} />
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default AddEditProgramDialog;
